import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Body,
  Link,
  Menu,
  MenuItem,
  MenuItems,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch } from 'src/ui/app/hooks';
import {
  ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU,
  ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM,
  ORGANIZATION_ENDPOINT_TABLE_ROW_NAME,
} from 'src/ui/constants/testIds';
import { SduMenuActions } from 'src/ui/containers/endpoints/SduMenuActions';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { getEndpointUrl } from 'src/ui/containers/endpoints/utils/getEndpointUrl';
import { useDownloadLogs } from 'src/ui/containers/endpoints/utils/useDownloadLogs';
import { useHasReadLogsPermissions } from 'src/ui/containers/endpoints/utils/useHasReadLogsPermissions';
import { useSduTrackingStatus } from 'src/ui/containers/endpoints/utils/useSduTrackingStatus';
import { editConnection } from 'src/ui/features/connection';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { Connection, useGetStripeSessionUrlMutation } from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useEndpointVersionGate } from 'src/ui/hooks/endpoint';
import { useIsOrganizationAdmin } from 'src/ui/hooks/organization';
import { useValidateConnection } from 'src/ui/hooks/validateConnection';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute, buildRoute } from 'src/ui/utils/buildRoute';
import { redirectToUrl } from 'src/ui/utils/window';

export interface OrganizationEndpointRowProps {
  connection: Connection;
}

export const OrganizationEndpointRow: React.VFC<{ connection: Connection }> = ({
  connection,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { org } = useParams<{ org: string }>();

  const { data: profile } = useProfile();
  const isAdmin = useIsOrganizationAdmin(org);
  const validateConnection = useValidateConnection();

  const { mutate: getStripeSessionUrl } = useGetStripeSessionUrlMutation(
    graphQLClient,
    {
      onSuccess: (data) => {
        redirectToUrl(data?.getStripeSessionUrl?.url || '/');
      },
    }
  );

  const isDownloadLogsSupported = useEndpointVersionGate(connection, '11.1.0');
  const isDownloadVoiceboxReportSupported = useEndpointVersionGate(
    connection,
    '10.0.0'
  );
  const {
    isSduSupported,
    disabled: isSduSyncDisabled,
    inProgress: isSduSyncInProgress,
  } = useSduTrackingStatus(connection);
  const canReadLogs = useHasReadLogsPermissions(
    connection,
    isDownloadLogsSupported ||
      isDownloadVoiceboxReportSupported ||
      isSduSupported
  );

  const [isDownloadingLogs, setIsDownloadingLogs] = React.useState(false);
  const downloadLogs = useDownloadLogs(connection);

  const handleClick = () => {
    history.push(buildRoute(org, connection.index));
  };

  const handleMenuInteraction = (isOpening: boolean) => {
    if (isOpening) {
      validateConnection(connection);
    }
  };

  const handleEditOrgEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.EDIT_ENDPOINT }));
  };

  const handleEditMyEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.EDIT_MY_ORGANIZATION_ENDPOINT }));
  };

  const handleManageUsers = () => {
    history.push(buildDashboardRoute(org, 'connections', connection.index));
  };

  // TODO: public orgs support
  // istanbul ignore next
  const _handleManageBilling = () => {
    getStripeSessionUrl({});
  };

  const handleDiagnostic = () => {
    history.push(buildRoute(org, connection.index, 'diagnostic'));
  };

  const handleDownloadLogs = async () => {
    setIsDownloadingLogs(true);
    await downloadLogs();
    if (isMounted.current) {
      setIsDownloadingLogs(false);
    }
  };

  const handleDownloadVoiceboxReport = () => {
    dispatch(
      openedDialog({
        type: DialogType.VOICEBOX_RESPONSE_REPORT,
        data: { connection },
      })
    );
  };

  const handleDeleteOrgEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.DELETE_ENDPOINT }));
  };

  const handleDeleteMyEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(
      openedDialog({ type: DialogType.DELETE_MY_ORGANIZATION_ENDPOINT })
    );
  };

  // TODO: public orgs support
  // istanbul ignore next
  const _handleCancelCloud = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.CANCEL_ENDPOINT }));
  };

  const authType = getEndpointAuthType(connection);
  const isOrgEndpoint =
    connection.access?.organization && !connection.access?.user_is_private;
  const isMyEndpoint =
    connection.access?.user_is_private &&
    connection.access?.user?.username === profile?.profile?.username;
  const isStardogCloudOrFree =
    connection.isStardogCloud || connection.isStardogFree;

  const actions = (
    <>
      {isAdmin && isOrgEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleEditOrgEndpoint}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'edit-endpoint'
          )}
          text={copy.components.endpoints.endpointTable.actions.editEndpoint}
        />
      ) : null}
      {isMyEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleEditMyEndpoint}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'edit-my-endpoint'
          )}
          text={copy.components.endpoints.endpointTable.actions.editEndpoint}
        />
      ) : null}
      {isAdmin && isOrgEndpoint && !connection.usesOrgSso ? (
        <MenuItem
          leftIcon={null}
          onClick={handleManageUsers}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'manage-users'
          )}
          text={copy.components.endpoints.endpointTable.actions.manageUsers}
        />
      ) : null}
      {/* TODO: public orgs support (cloud purchase) */}
      {/* {isAdmin && isStardogCloudOrFree ? (
        <MenuItem
          disabled={Boolean(connection.isAllocating)}
          leftIcon={null}
          onClick={handleManageBilling}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'manage-billing'
          )}
          text={copy.components.endpoints.endpointTable.actions.manageBilling}
        />
      ) : null} */}
      <MenuItem
        leftIcon={null}
        onClick={handleDiagnostic}
        testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
          'view-diagnostics'
        )}
        text={copy.components.endpoints.endpointTable.actions.viewDiagnostics}
      />
      {isDownloadLogsSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadLogsPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs || isDownloadingLogs}
            leftIcon={null}
            onClick={handleDownloadLogs}
            testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-logs'
            )}
            text={copy.components.endpoints.endpointTable.actions.downloadLogs}
          />
        </Tooltip>
      ) : null}
      {isDownloadVoiceboxReportSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadVoiceboxReportPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs}
            leftIcon={null}
            onClick={handleDownloadVoiceboxReport}
            testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-voicebox-report'
            )}
            text={
              copy.components.endpoints.endpointTable.actions
                .downloadVoiceboxReport
            }
          />
        </Tooltip>
      ) : null}
      {isSduSupported ? (
        <SduMenuActions
          canReadLogs={canReadLogs}
          connection={connection}
          downloadTestId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'download-sdu'
          )}
          isSyncDisabled={isSduSyncDisabled}
          isSyncInProgress={isSduSyncInProgress}
          syncTestId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'sync-sdu'
          )}
        />
      ) : null}
      {/* TODO: public orgs support (cloud purchase) */}
      {/* {isAdmin && isOrgEndpoint && isStardogCloudOrFree ? (
        <MenuItem
          leftIcon={null}
          onClick={handleCancelCloud}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'cancel-cloud'
          )}
          text={copy.components.endpoints.endpointTable.actions.cancelCloud}
        />
      ) : null} */}
      {isAdmin && isOrgEndpoint && !isStardogCloudOrFree ? (
        <MenuItem
          leftIcon={null}
          onClick={handleDeleteOrgEndpoint}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'delete-endpoint'
          )}
          text={copy.components.endpoints.endpointTable.actions.deleteEndpoint}
        />
      ) : null}
      {isMyEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleDeleteMyEndpoint}
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'delete-my-endpoint'
          )}
          text={copy.components.endpoints.endpointTable.actions.deleteEndpoint}
        />
      ) : null}
    </>
  );

  let authLabel;
  if (authType === 'org-sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.orgSSO;
  } else if (authType === 'sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.sso;
  } else if (authType === 'browser-auth') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.browser;
  } else {
    authLabel = copy.components.endpoints.endpointTable.row.auth.username(
      connection.username || ''
    );
  }

  return (
    <>
      <td className="organization-table-row-endpoint-name">
        <Link
          onClick={handleClick}
          size="md"
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_NAME(
            connection.id,
            connection.index
          )}
        >
          {connection.name}
        </Link>
      </td>
      <td className="organization-table-row-endpoint-url">
        <Body size="md">{getEndpointUrl(connection)}</Body>
      </td>
      <td className="organization-table-row-endpoint-auth">
        <Body size="md">{authLabel}</Body>
      </td>
      <td className="data-table-row-actions">
        <Menu
          leftIcon={OverflowMenuHorizontal}
          menu={
            <MenuItems className="data-table-menu-items">{actions}</MenuItems>
          }
          onInteraction={handleMenuInteraction}
          size="sm"
          testId={ORGANIZATION_ENDPOINT_TABLE_ROW_ACTIONS_MENU(
            connection.id,
            connection.index
          )}
        />
      </td>
    </>
  );
};
